import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Plus, Edit2, Trash2, Eye, EyeOff, Save, X, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";

function generateSlug(title) {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

export default function BlogAdmin() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    excerpt: "",
    content: "",
    author: "Soundope Team",
  });

  // Load posts from Supabase
  const fetchPosts = async () => {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setPosts(data || []);
    } catch (error) {
      console.error("Failed to fetch posts:", error);
      toast.error("Failed to load blog posts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  const handleCreate = () => {
    setIsCreating(true);
    setEditingPost(null);
    setFormData({
      title: "",
      excerpt: "",
      content: "",
      author: "Soundope Team",
    });
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setIsCreating(false);
    setFormData({
      title: post.title,
      excerpt: post.excerpt || "",
      content: post.content,
      author: post.author,
    });
  };

  const handleCancel = () => {
    setEditingPost(null);
    setIsCreating(false);
    setFormData({ title: "", excerpt: "", content: "", author: "Soundope Team" });
  };

  const handleSave = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error("Title and content are required");
      return;
    }

    setSaving(true);

    try {
      if (isCreating) {
        // Create new post
        const { error } = await supabase
          .from('blog_posts')
          .insert({
            title: formData.title.trim(),
            slug: generateSlug(formData.title),
            excerpt: formData.excerpt.trim() || null,
            content: formData.content,
            author: formData.author.trim() || "Soundope Team",
            published: false,
          });

        if (error) throw error;
        toast.success("Post created! Set to draft by default.");
      } else if (editingPost) {
        // Update existing post
        const { error } = await supabase
          .from('blog_posts')
          .update({
            title: formData.title.trim(),
            slug: generateSlug(formData.title),
            excerpt: formData.excerpt.trim() || null,
            content: formData.content,
            author: formData.author.trim() || "Soundope Team",
          })
          .eq('id', editingPost.id);

        if (error) throw error;
        toast.success("Post updated!");
      }

      await fetchPosts();
      handleCancel();
    } catch (error) {
      console.error("Save error:", error);
      toast.error(error.message || "Failed to save post");
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePublish = async (post) => {
    try {
      const { error } = await supabase
        .from('blog_posts')
        .update({ published: !post.published })
        .eq('id', post.id);

      if (error) throw error;

      await fetchPosts();
      toast.success(post.published ? "Post unpublished" : "Post published!");
    } catch (error) {
      console.error("Toggle publish error:", error);
      toast.error("Failed to update post");
    }
  };

  const handleDelete = async (post) => {
    if (!confirm(`Delete "${post.title}"? This cannot be undone.`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('blog_posts')
        .delete()
        .eq('id', post.id);

      if (error) throw error;

      await fetchPosts();
      toast.success("Post deleted");
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete post");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-foreground">Blog Posts</h3>
        {!isCreating && !editingPost && (
          <Button
            onClick={handleCreate}
            variant="outline"
            size="sm"
            className="gap-2"
          >
            <Plus className="w-4 h-4" />
            New Post
          </Button>
        )}
      </div>

      {/* Create/Edit Form */}
      {(isCreating || editingPost) && (
        <div className="bg-card rounded-xl p-4 space-y-4 border border-border">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-muted-foreground">
              {isCreating ? "New Post" : "Edit Post"}
            </h4>
            <button onClick={handleCancel} className="text-muted-foreground hover:text-foreground">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="space-y-3">
            <Input
              placeholder="Post title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="bg-background border-border"
            />
            <Input
              placeholder="Author name"
              value={formData.author}
              onChange={(e) => setFormData({ ...formData, author: e.target.value })}
              className="bg-background border-border"
            />
            <Textarea
              placeholder="Short excerpt (optional - auto-generated from content if empty)"
              value={formData.excerpt}
              onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
              className="bg-background border-border min-h-[60px]"
            />
            <Textarea
              placeholder="Post content..."
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              className="bg-background border-border min-h-[200px]"
            />
          </div>

          <div className="flex gap-2">
            <Button onClick={handleSave} size="sm" className="gap-2" disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save
            </Button>
            <Button onClick={handleCancel} variant="ghost" size="sm" disabled={saving}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Posts List */}
      <div className="space-y-2 max-h-[400px] overflow-y-auto">
        {posts.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            No blog posts yet. Create your first one!
          </p>
        ) : (
          posts.map((post) => (
            <div
              key={post.id}
              className="bg-card rounded-xl p-3 flex items-center justify-between gap-3 border border-border"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h4 className="text-sm font-medium text-foreground truncate">
                    {post.title}
                  </h4>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${
                      post.published
                        ? "bg-green-500/20 text-green-400"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {post.published ? "Published" : "Draft"}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground truncate">{post.excerpt}</p>
              </div>

              <div className="flex items-center gap-1 flex-shrink-0">
                <Link
                  to={`/blog/${post.slug}`}
                  target="_blank"
                  className="p-2 text-muted-foreground hover:text-primary transition-colors"
                  title="View post"
                >
                  <ExternalLink className="w-4 h-4" />
                </Link>
                <button
                  onClick={() => handleTogglePublish(post)}
                  className="p-2 text-muted-foreground hover:text-foreground transition-colors"
                  title={post.published ? "Unpublish" : "Publish"}
                >
                  {post.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                <button
                  onClick={() => handleEdit(post)}
                  className="p-2 text-muted-foreground hover:text-foreground transition-colors"
                  title="Edit"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(post)}
                  className="p-2 text-muted-foreground hover:text-destructive transition-colors"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}